/**
 * Client-side SPC valuation preview — mirrors backend computeSpcValuationAmounts.
 * Authority remains on the server at create/patch; this is for live UI totals only.
 */

export type SpcValuationPreviewInput = {
  workGross: number;
  materialsOnSite?: number;
  dayworksAmount?: number;
  attendancePct?: number;
  includeMaterialsOnSite?: boolean;
  freeIssueAmount?: number;
  advanceRecoveryRatePct?: number;
  advanceRecoveryAmount?: number;
  remainingAdvanceBalance?: number;
  retentionRatePct?: number;
  retentionWithheldAmount?: number;
  retentionReleasedAmount?: number;
  setOffAmount?: number;
};

export type SpcValuationPreview = {
  workGross: number;
  materialsOnSite: number;
  dayworksAmount: number;
  attendanceAmount: number;
  freeIssueExcluded: number;
  grossBeforeCommercial: number;
  advanceRecovery: number;
  retentionWithheld: number;
  retentionReleased: number;
  setOffAmount: number;
  netApplied: number;
};

function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export function previewSpcValuation(input: SpcValuationPreviewInput): SpcValuationPreview {
  const workGross = round2(Math.max(0, input.workGross || 0));
  const dayworks = round2(Math.max(0, input.dayworksAmount || 0));
  const mos = round2(Math.max(0, input.materialsOnSite || 0));
  const includeMos =
    input.includeMaterialsOnSite != null ? Boolean(input.includeMaterialsOnSite) : mos > 0;
  const freeIssue = round2(Math.max(0, input.freeIssueAmount || 0));
  const grossBase = round2(workGross + dayworks + (includeMos ? mos : 0));
  const attendance = round2((grossBase * Math.max(0, input.attendancePct || 0)) / 100);
  const grossBeforeCommercial = round2(grossBase + attendance);

  let advance = 0;
  if (input.advanceRecoveryAmount != null) {
    advance = round2(Math.max(0, input.advanceRecoveryAmount));
  } else if (input.advanceRecoveryRatePct != null) {
    advance = round2((grossBeforeCommercial * input.advanceRecoveryRatePct) / 100);
  }
  if (input.remainingAdvanceBalance != null) {
    advance = round2(Math.min(advance, Math.max(0, input.remainingAdvanceBalance)));
  }

  let retention = 0;
  if (input.retentionWithheldAmount != null) {
    retention = round2(Math.max(0, input.retentionWithheldAmount));
  } else if (input.retentionRatePct != null) {
    retention = round2((grossBeforeCommercial * input.retentionRatePct) / 100);
  }

  const release = round2(Math.max(0, input.retentionReleasedAmount || 0));
  const setOff = round2(Math.max(0, input.setOffAmount || 0));
  const net = round2(grossBeforeCommercial - advance - retention + release - setOff);

  return {
    workGross,
    materialsOnSite: includeMos ? mos : 0,
    dayworksAmount: dayworks,
    attendanceAmount: attendance,
    freeIssueExcluded: freeIssue,
    grossBeforeCommercial,
    advanceRecovery: advance,
    retentionWithheld: retention,
    retentionReleased: release,
    setOffAmount: setOff,
    netApplied: net,
  };
}
